import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { fetchMyOrder } from '../api/customer';
import { useCustomerAuth } from '../state/customerAuth';
import '../styles/account.css';

export function CustomerOrderDetailPage() {
  const { orderId } = useParams();
  const { token, isAuthenticated, booting } = useCustomerAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!token || !orderId) return;
    let c = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const data = await fetchMyOrder(token, orderId);
        if (!c) setOrder(data);
      } catch (e) {
        if (!c) setErr(e instanceof Error ? e.message : 'Failed to load order');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [token, orderId]);

  if (booting) {
    return (
      <main className="accountDash accountDash--center">
        <p className="accountDash__muted">Loading…</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/account" replace />;
  }

  if (!loading && !order && !err) {
    return <Navigate to="/account/dashboard" replace />;
  }

  const lines = Array.isArray(order?.lines) ? order.lines : [];
  const tracking = order?.tracking;

  return (
    <main className="accountDash">
      <div className="accountDash__inner">
        <p className="accountDash__back">
          <Link to="/account/dashboard" className="accountDash__link">
            ← Order history
          </Link>
        </p>

        {loading ? <p className="accountDash__muted">Loading order…</p> : null}
        {err ? <p className="accountDash__err">{err}</p> : null}

        {order ? (
          <>
            <header className="accountDash__head">
              <div>
                <h1 className="accountDash__title">Order {order.number}</h1>
                <p className="accountDash__muted">
                  Placed {order.placed_at || order.created_at || '—'} · Payment{' '}
                  <strong>{order.payment_status || '—'}</strong>
                </p>
              </div>
              <div className="accountDash__actions">
                <span className="accountDash__pill accountDash__pill--lg">{order.status}</span>
              </div>
            </header>

            <section className="accountDash__card accountDash__mb">
              <h2 className="accountDash__h2">Tracking &amp; status</h2>
              <p className="accountDash__muted">{tracking?.summary || order.status}</p>
              <dl className="accountDash__dl">
                <div>
                  <dt>Order number</dt>
                  <dd>
                    <code>{tracking?.number || order.number}</code>
                  </dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{tracking?.status || order.status}</dd>
                </div>
                <div>
                  <dt>Placed</dt>
                  <dd>{tracking?.placed_at || order.placed_at || '—'}</dd>
                </div>
              </dl>
            </section>

            <section className="accountDash__card accountDash__mb">
              <h2 className="accountDash__h2">Totals</h2>
              <dl className="accountDash__dl">
                {order.subtotal != null ? (
                  <div>
                    <dt>Subtotal</dt>
                    <dd>{Number(order.subtotal).toFixed(2)}</dd>
                  </div>
                ) : null}
                {order.shipping_total != null ? (
                  <div>
                    <dt>Shipping</dt>
                    <dd>{Number(order.shipping_total).toFixed(2)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Total</dt>
                  <dd>
                    <strong>{order.total_formatted || Number(order.total).toFixed(2)}</strong>
                  </dd>
                </div>
                <div>
                  <dt>Payment method</dt>
                  <dd>{order.payment_method || '—'}</dd>
                </div>
              </dl>
            </section>

            <section className="accountDash__card">
              <h2 className="accountDash__h2">Line items</h2>
              {lines.length === 0 ? (
                <p className="accountDash__muted">No line details stored for this order.</p>
              ) : (
                <div className="accountDash__tableWrap">
                  <table className="accountDash__table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Line total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((row) => (
                        <tr key={row.id}>
                          <td>{row.name}</td>
                          <td>
                            <code>{row.sku}</code>
                          </td>
                          <td>{row.quantity}</td>
                          <td>{Number(row.unit_price).toFixed(2)}</td>
                          <td>{Number(row.line_total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : null}
      </div>
    </main>
  );
}
